var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { Expose, Type } from 'class-transformer';
import { IsArray, IsBoolean, IsDate, IsIn, IsInt, IsOptional, IsString, IsUUID, MaxLength, Min, ValidateNested, } from 'class-validator';
/**
 * Stable ids for the plan-scoped delivery-fact constraints. **Advisory, never
 * blocking** — consistent with the guide-and-judge stance the Heilmittelkatalog
 * validation already takes.
 */
export const DELIVERY_FACT_CONSTRAINT_IDS = {
    /**
     * § 7a Abs. 5 of the Vertrag nach § 125 Abs. 1: telemedizinische Leistungen are
     * capped against the *verordnete* Behandlungseinheiten — bis zur Hälfte for
     * allgemeine Krankengymnastik (Einzel- und Gruppenbehandlung) and KG-Muko,
     * bis zu 3 Behandlungseinheiten for KG-ZNS nach Bobath (Kinder und
     * Erwachsene), bis zu 1 Behandlungseinheit for Manuelle Therapie.
     */
    TELEMEDIZIN_QUOTA_EXCEEDED: 'telemedizin_quota_exceeded',
    /**
     * § 7a Abs. 6: the video mode must be agreed **in writing**, and either side
     * may refuse. A telemedizinische Leistung recorded against a plan with no
     * recorded agreement.
     */
    TELEMEDIZIN_WITHOUT_VIDEO_CONSENT: 'telemedizin_without_video_consent',
    /**
     * § 7a Abs. 6: the patient may revoke mid-series. A telemedizinische Leistung
     * recorded on a Leistungstag at or after the revocation.
     */
    TELEMEDIZIN_AFTER_VIDEO_CONSENT_REVOKED: 'telemedizin_after_video_consent_revoked',
};
export class DeliveryFactFindingDto {
    constraintId;
    severity;
    params;
}
__decorate([
    IsString(),
    Expose(),
    __metadata("design:type", String)
], DeliveryFactFindingDto.prototype, "constraintId", void 0);
__decorate([
    IsString(),
    Expose(),
    __metadata("design:type", String)
], DeliveryFactFindingDto.prototype, "severity", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], DeliveryFactFindingDto.prototype, "params", void 0);
/** One Positionsnummer a POSITION_CHOICE fact can resolve to. */
export class DeliveryFactCandidateDto {
    /** The **fact** stored as the value — never the Positionsnummer. */
    code;
    posNumber;
    label;
    /** A telemedizinische Leistung — carries the „TM" marker on the paper row. */
    telemedizin;
}
__decorate([
    IsString(),
    Expose(),
    __metadata("design:type", String)
], DeliveryFactCandidateDto.prototype, "code", void 0);
__decorate([
    IsString(),
    Expose(),
    __metadata("design:type", String)
], DeliveryFactCandidateDto.prototype, "posNumber", void 0);
__decorate([
    IsString(),
    Expose(),
    __metadata("design:type", String)
], DeliveryFactCandidateDto.prototype, "label", void 0);
__decorate([
    IsBoolean(),
    Expose(),
    __metadata("design:type", Boolean)
], DeliveryFactCandidateDto.prototype, "telemedizin", void 0);
/** A recorded value that has been struck through — kept, never erased. */
export class DeliveryFactSupersededValueDto {
    id;
    /** The struck value, already rendered (`3`, `MLD-45`, a candidate label). */
    label;
    recordedAt;
    recordedByName;
    struckOutAt;
}
__decorate([
    IsUUID(),
    Expose(),
    __metadata("design:type", String)
], DeliveryFactSupersededValueDto.prototype, "id", void 0);
__decorate([
    IsString(),
    Expose(),
    __metadata("design:type", String)
], DeliveryFactSupersededValueDto.prototype, "label", void 0);
__decorate([
    IsDate(),
    Type(() => Date),
    Expose(),
    __metadata("design:type", Date)
], DeliveryFactSupersededValueDto.prototype, "recordedAt", void 0);
__decorate([
    IsString(),
    IsOptional(),
    Expose(),
    __metadata("design:type", Object)
], DeliveryFactSupersededValueDto.prototype, "recordedByName", void 0);
__decorate([
    IsDate(),
    Type(() => Date),
    Expose(),
    __metadata("design:type", Date)
], DeliveryFactSupersededValueDto.prototype, "struckOutAt", void 0);
/**
 * The previous session's answer, offered as a **visible suggestion** carrying its
 * source date. Uncommitted until the Therapeut saves — no value is ever asserted
 * to a Kostenträger without a human act. Drawn from the most recent
 * chronologically-earlier Leistungstag on the same Behandlungsplan that actually
 * has a value, so one skipped session does not silence it for the rest of the
 * series. The **first Leistungstag of a Behandlungsplan is never prefilled**.
 */
export class DeliveryFactSuggestionDto {
    valueInt;
    valueCode;
    /** Already-rendered value for the hint (`„wie am 12.08."`). */
    label;
    sourceDate;
}
__decorate([
    IsInt(),
    IsOptional(),
    Expose(),
    __metadata("design:type", Object)
], DeliveryFactSuggestionDto.prototype, "valueInt", void 0);
__decorate([
    IsString(),
    IsOptional(),
    Expose(),
    __metadata("design:type", Object)
], DeliveryFactSuggestionDto.prototype, "valueCode", void 0);
__decorate([
    IsString(),
    Expose(),
    __metadata("design:type", String)
], DeliveryFactSuggestionDto.prototype, "label", void 0);
__decorate([
    IsDate(),
    Type(() => Date),
    Expose(),
    __metadata("design:type", Date)
], DeliveryFactSuggestionDto.prototype, "sourceDate", void 0);
/** One question a Leistungstag actually raises, with its answer if recorded. */
export class DeliveryFactQuestionDto {
    kindCode;
    scope;
    valueType;
    /** Null for a VISIT-scoped kind. */
    prescribedCatalogOptionId;
    candidates;
    /**
     * The candidate that bills while the question is unanswered — the prescribed
     * form, a Vorbelegung. Null where an unanswered question bills nothing.
     */
    defaultCode;
    /** Upper bound for an `INT` input; null for the other value types. */
    maxInt;
    valueInt;
    valueCode;
    /** Provenance of the recorded value: `author_id` + `created_at`, nothing more. */
    recordedAt;
    recordedByName;
    /**
     * Whole days between the recording and the treatment day — the
     * retrospectivity, **computed rather than asserted**. Null while unanswered.
     */
    recordedDaysAfterTreatment;
    supersededValues;
    suggestion;
}
__decorate([
    IsString(),
    Expose(),
    __metadata("design:type", String)
], DeliveryFactQuestionDto.prototype, "kindCode", void 0);
__decorate([
    IsString(),
    Expose(),
    __metadata("design:type", String)
], DeliveryFactQuestionDto.prototype, "scope", void 0);
__decorate([
    IsString(),
    Expose(),
    __metadata("design:type", String)
], DeliveryFactQuestionDto.prototype, "valueType", void 0);
__decorate([
    IsUUID(),
    IsOptional(),
    Expose(),
    __metadata("design:type", Object)
], DeliveryFactQuestionDto.prototype, "prescribedCatalogOptionId", void 0);
__decorate([
    IsArray(),
    ValidateNested({ each: true }),
    Type(() => DeliveryFactCandidateDto),
    Expose(),
    __metadata("design:type", Array)
], DeliveryFactQuestionDto.prototype, "candidates", void 0);
__decorate([
    IsString(),
    IsOptional(),
    Expose(),
    __metadata("design:type", Object)
], DeliveryFactQuestionDto.prototype, "defaultCode", void 0);
__decorate([
    IsInt(),
    IsOptional(),
    Expose(),
    __metadata("design:type", Object)
], DeliveryFactQuestionDto.prototype, "maxInt", void 0);
__decorate([
    IsInt(),
    IsOptional(),
    Expose(),
    __metadata("design:type", Object)
], DeliveryFactQuestionDto.prototype, "valueInt", void 0);
__decorate([
    IsString(),
    IsOptional(),
    Expose(),
    __metadata("design:type", Object)
], DeliveryFactQuestionDto.prototype, "valueCode", void 0);
__decorate([
    IsDate(),
    IsOptional(),
    Type(() => Date),
    Expose(),
    __metadata("design:type", Object)
], DeliveryFactQuestionDto.prototype, "recordedAt", void 0);
__decorate([
    IsString(),
    IsOptional(),
    Expose(),
    __metadata("design:type", Object)
], DeliveryFactQuestionDto.prototype, "recordedByName", void 0);
__decorate([
    IsInt(),
    IsOptional(),
    Expose(),
    __metadata("design:type", Object)
], DeliveryFactQuestionDto.prototype, "recordedDaysAfterTreatment", void 0);
__decorate([
    IsArray(),
    ValidateNested({ each: true }),
    Type(() => DeliveryFactSupersededValueDto),
    Expose(),
    __metadata("design:type", Array)
], DeliveryFactQuestionDto.prototype, "supersededValues", void 0);
__decorate([
    ValidateNested(),
    IsOptional(),
    Type(() => DeliveryFactSuggestionDto),
    Expose(),
    __metadata("design:type", Object)
], DeliveryFactQuestionDto.prototype, "suggestion", void 0);
/**
 * One billable line: a masked Positionsnummer with its Anzahl (ADR-0030).
 *
 * **Masked, always.** The `X`→digit substitution is the §302 emitter's and never
 * lands on a catalogue row — the masked form is the join key between
 * Heilmittelkatalog and Preisliste, and it is what the Preisliste shows.
 */
export class AbrechnungspositionDto {
    posNumber;
    anzahl;
}
__decorate([
    IsString(),
    Expose(),
    __metadata("design:type", String)
], AbrechnungspositionDto.prototype, "posNumber", void 0);
__decorate([
    IsInt(),
    Expose(),
    __metadata("design:type", Number)
], AbrechnungspositionDto.prototype, "anzahl", void 0);
/** Every reason, in one place — the `@IsIn` list and the union stay in step. */
export const RESOLUTION_GAP_REASONS = [
    'MISSING_FACT',
    'NO_CANDIDATE_MATCH',
    'AMBIGUOUS_OPTION',
    'OPTION_NOT_IN_RELEASE',
];
export class ResolutionGapDto {
    reason;
    /** The kind that would have answered it; null where no kind can. */
    kindCode;
}
__decorate([
    IsIn(RESOLUTION_GAP_REASONS),
    Expose(),
    __metadata("design:type", String)
], ResolutionGapDto.prototype, "reason", void 0);
__decorate([
    IsString(),
    IsOptional(),
    Expose(),
    __metadata("design:type", Object)
], ResolutionGapDto.prototype, "kindCode", void 0);
/** One prescribed Heilmittel delivered on a Leistungstag — a paper row. */
export class LeistungstagHeilmittelDto {
    prescribedCatalogOptionId;
    /** Maßnahme — the Heilmittelkatalog option name, or the concept's abbreviation. */
    label;
    /** Behandlungseinheiten delivered for this Heilmittel on this day. */
    unitCount;
    /**
     * What this Heilmittel bills on this day, derived from the day's facts and
     * never stored (#1428). Empty where the resolver abstained — `gaps` then says
     * why, in a named case rather than a null.
     */
    positions;
    gaps;
    /** The „TM" marker of § 5 Abs. 1's Verordnungsrückseite row. */
    telemedizin;
    questions;
}
__decorate([
    IsUUID(),
    IsOptional(),
    Expose(),
    __metadata("design:type", Object)
], LeistungstagHeilmittelDto.prototype, "prescribedCatalogOptionId", void 0);
__decorate([
    IsString(),
    Expose(),
    __metadata("design:type", String)
], LeistungstagHeilmittelDto.prototype, "label", void 0);
__decorate([
    IsInt(),
    Expose(),
    __metadata("design:type", Number)
], LeistungstagHeilmittelDto.prototype, "unitCount", void 0);
__decorate([
    IsArray(),
    ValidateNested({ each: true }),
    Type(() => AbrechnungspositionDto),
    Expose(),
    __metadata("design:type", Array)
], LeistungstagHeilmittelDto.prototype, "positions", void 0);
__decorate([
    IsArray(),
    ValidateNested({ each: true }),
    Type(() => ResolutionGapDto),
    Expose(),
    __metadata("design:type", Array)
], LeistungstagHeilmittelDto.prototype, "gaps", void 0);
__decorate([
    IsBoolean(),
    Expose(),
    __metadata("design:type", Boolean)
], LeistungstagHeilmittelDto.prototype, "telemedizin", void 0);
__decorate([
    IsArray(),
    ValidateNested({ each: true }),
    Type(() => DeliveryFactQuestionDto),
    Expose(),
    __metadata("design:type", Array)
], LeistungstagHeilmittelDto.prototype, "questions", void 0);
/** One Leistungstag of the Behandlungsplan — the grain of the whole record. */
export class LeistungstagDto {
    appointmentId;
    date;
    heilmittel;
    /**
     * What the whole day bills: identical Positionsnummern combined across the
     * day's Heilmittel, which is the grain an EHE-Segment is written at (#1241).
     */
    positions;
    /**
     * Every abstention of the day, across its Heilmittel. Non-empty ⇒ the day is
     * not fully billable — one unresolvable Heilmittel never suppresses its
     * neighbours' positions.
     */
    gaps;
    /** Applicable questions on this day that have no recorded answer. */
    missingFactCount;
}
__decorate([
    IsUUID(),
    Expose(),
    __metadata("design:type", String)
], LeistungstagDto.prototype, "appointmentId", void 0);
__decorate([
    IsDate(),
    Type(() => Date),
    Expose(),
    __metadata("design:type", Date)
], LeistungstagDto.prototype, "date", void 0);
__decorate([
    IsArray(),
    ValidateNested({ each: true }),
    Type(() => LeistungstagHeilmittelDto),
    Expose(),
    __metadata("design:type", Array)
], LeistungstagDto.prototype, "heilmittel", void 0);
__decorate([
    IsArray(),
    ValidateNested({ each: true }),
    Type(() => AbrechnungspositionDto),
    Expose(),
    __metadata("design:type", Array)
], LeistungstagDto.prototype, "positions", void 0);
__decorate([
    IsArray(),
    ValidateNested({ each: true }),
    Type(() => ResolutionGapDto),
    Expose(),
    __metadata("design:type", Array)
], LeistungstagDto.prototype, "gaps", void 0);
__decorate([
    IsInt(),
    Expose(),
    __metadata("design:type", Number)
], LeistungstagDto.prototype, "missingFactCount", void 0);
// --- write side ---------------------------------------------------------------
export class RecordDeliveryFactInputDto {
    kindCode;
    /** Null/omitted for a VISIT-scoped kind. */
    prescribedCatalogOptionId;
    valueInt;
    valueCode;
}
__decorate([
    IsString(),
    MaxLength(64),
    Expose(),
    __metadata("design:type", String)
], RecordDeliveryFactInputDto.prototype, "kindCode", void 0);
__decorate([
    IsUUID(),
    IsOptional(),
    Expose(),
    __metadata("design:type", Object)
], RecordDeliveryFactInputDto.prototype, "prescribedCatalogOptionId", void 0);
__decorate([
    IsInt(),
    Min(0),
    IsOptional(),
    Expose(),
    __metadata("design:type", Object)
], RecordDeliveryFactInputDto.prototype, "valueInt", void 0);
__decorate([
    IsString(),
    MaxLength(64),
    IsOptional(),
    Expose(),
    __metadata("design:type", Object)
], RecordDeliveryFactInputDto.prototype, "valueCode", void 0);
/**
 * Record the delivery facts for one Leistungstag — a single command, superseding
 * any struck-through predecessors. An entry whose value is null/omitted **strikes
 * the recorded fact through without a successor** (the Therapeut retracting an
 * answer); an entry absent from the array leaves its fact untouched.
 */
export class RecordDeliveryFactsDto {
    facts;
}
__decorate([
    IsArray(),
    ValidateNested({ each: true }),
    Type(() => RecordDeliveryFactInputDto),
    Expose(),
    __metadata("design:type", Array)
], RecordDeliveryFactsDto.prototype, "facts", void 0);
/**
 * The patient's written agreement to video treatment (§ 7a Abs. 6), recorded
 * per Behandlungsplan. Two dates rather than a state enum, because the
 * revocation is what splits the series: a Leistungstag before the revocation is
 * covered and one on or after it is not, and only dates can say that.
 *
 * `agreedOn: null` means never recorded — a telemedizinische Leistung then has
 * no basis at all, which the constraint reports (advisory, never blocking).
 */
export class SetVideoConsentDto {
    agreedOn;
    revokedOn;
}
__decorate([
    IsDate(),
    Type(() => Date),
    IsOptional(),
    Expose(),
    __metadata("design:type", Object)
], SetVideoConsentDto.prototype, "agreedOn", void 0);
__decorate([
    IsDate(),
    Type(() => Date),
    IsOptional(),
    Expose(),
    __metadata("design:type", Object)
], SetVideoConsentDto.prototype, "revokedOn", void 0);
//# sourceMappingURL=delivery-fact.dto.js.map